<template>
  <Dashboard>
    <div class="craigslist-area">
      <form class="myform" @submit.prevent="saveSettings">
        <div class="row">
          <!-- Left: engine + channels -->
          <div class="col-md-6">
            <div class="setting-text">
              <h4>Auto Posting Engine</h4>
              <p>
                When enabled, every vehicle in your inventory (current and newly
                added) is automatically posted to the channels you select below.
                Each vehicle is posted only once per channel.
              </p>
            </div>

            <div v-if="!offered" class="alert-text">
              <h5>
                <img class="img-fluid" src="@/assets/img/warning.svg" alt="" />
                Not available
              </h5>
              <p>
                Auto posting is not enabled for your account yet. Please contact
                support to have it activated.
              </p>
            </div>

            <template v-else>
              <div class="form-group">
                <label class="checks">
                  <input type="checkbox" v-model="settings.auto_posting_enabled" />
                  <span class="checkmark"></span>
                  <strong>&nbsp;Enable auto posting</strong>
                </label>
              </div>

              <div class="form-group" style="margin-top:15px;">
                <label>Post new inventory to:</label>

                <div style="margin-top:8px;">
                  <label class="checks">
                    <input
                      type="checkbox"
                      v-model="settings.auto_post_to_facebook"
                      :disabled="!facebookConnected"
                    />
                    <span class="checkmark"></span>
                    &nbsp;Facebook Page
                    <span v-if="facebookConnected" class="badge-connected">
                      Connected<span v-if="facebookPageName"> &middot; {{ facebookPageName }}</span>
                    </span>
                    <span v-else class="badge-disconnected">
                      Not connected &mdash;
                      <router-link :to="{ name: 'FacebookSettings' }">connect</router-link>
                    </span>
                  </label>
                </div>

                <div style="margin-top:8px;">
                  <label class="checks">
                    <input
                      type="checkbox"
                      v-model="settings.auto_post_to_gmb"
                      :disabled="!gmbConnected"
                    />
                    <span class="checkmark"></span>
                    &nbsp;Google Business Profile
                    <span v-if="gmbConnected" class="badge-connected">Connected</span>
                    <span v-else class="badge-disconnected">
                      Not connected &mdash;
                      <router-link :to="{ name: 'GoogleMyBusiness' }">connect</router-link>
                    </span>
                  </label>
                </div>
              </div>
            </template>
          </div>

          <!-- Right: GMB location picker -->
          <div class="col-md-6" v-if="offered && settings.auto_post_to_gmb && gmbConnected">
            <div class="setting-text">
              <h4>Google Locations</h4>
              <p>Choose which business location(s) new vehicles should be posted to.</p>
            </div>
            <div v-if="availableGmbLocations.length === 0">
              <p>No locations found. Make sure you have selected locations on the Google Business Profile page.</p>
            </div>
            <div
              v-for="location in availableGmbLocations"
              :key="location.name"
              style="margin-top:8px;"
            >
              <label class="checks">
                <input
                  type="checkbox"
                  :value="location.name"
                  v-model="selectedGmbLocationNames"
                />
                <span class="checkmark"></span>
                &nbsp;{{ location.title || location.name }}
              </label>
            </div>
          </div>
        </div>

        <div class="form-row">
          <div class="col-md-12">
            <div class="cus-btn text-right">
              <router-link :to="{ name: 'Dashboard' }" class="cancle-btn">Back</router-link>
              <button v-if="offered" type="submit" class="send-btn">Submit</button>
            </div>
          </div>
        </div>
      </form>
    </div>
  </Dashboard>
</template>

<script>
import Dashboard from "./../../layouts/Dashboard.vue";
import axios from "axios";
import { onMounted, ref } from "vue";

export default {
  components: {
    Dashboard,
  },
  setup() {
    const dealerId = localStorage.getItem("dealer_id");

    const offered = ref(false);
    const facebookConnected = ref(false);
    const gmbConnected = ref(false);
    const facebookPageName = ref("");
    const availableGmbLocations = ref([]);
    const selectedGmbLocationNames = ref([]);

    const settings = ref({
      auto_posting_enabled: false,
      auto_post_to_facebook: false,
      auto_post_to_gmb: false,
    });

    const authHeaders = () => ({
      headers: { Authorization: "Bearer " + localStorage.getItem("auth_token") },
    });

    // Pull the dealer's saved GMB locations so they can pick which to auto-post to.
    const loadGmbLocations = async () => {
      await axios
        .post(
          axios.defaults.baseUrl + "getAllGMBLocations",
          { dealer_id: dealerId },
          authHeaders()
        )
        .then((response) => {
          availableGmbLocations.value = response.data.data || [];
        })
        .catch(() => {});
    };

    const loadSettings = async () => {
      await axios
        .get(
          axios.defaults.baseUrl + "getDealerAutoPostingSettings/" + dealerId,
          authHeaders()
        )
        .then((response) => {
          const s = response.data.settings;
          offered.value = s.auto_posting_offered == 1;
          facebookConnected.value = response.data.facebook_connected;
          gmbConnected.value = response.data.gmb_connected;
          facebookPageName.value = response.data.facebook_page_name || "";

          settings.value.auto_posting_enabled = s.auto_posting_enabled == 1;
          settings.value.auto_post_to_facebook = s.auto_post_to_facebook == 1;
          settings.value.auto_post_to_gmb = s.auto_post_to_gmb == 1;

          // Pre-select previously chosen GMB locations.
          selectedGmbLocationNames.value = (s.auto_post_gmb_locations || []).map(
            (loc) => loc.name
          );

          if (gmbConnected.value) {
            loadGmbLocations();
          }
        })
        .catch(() => {
          window.toast.fire({
            icon: "error",
            title: "Could not load auto posting settings.",
          });
        });
    };

    const saveSettings = async () => {
      // Build {name,title} pairs for the chosen GMB locations.
      const chosenLocations = availableGmbLocations.value
        .filter((loc) => selectedGmbLocationNames.value.includes(loc.name))
        .map((loc) => ({ name: loc.name, title: loc.title || "" }));

      if (
        settings.value.auto_posting_enabled &&
        !settings.value.auto_post_to_facebook &&
        !settings.value.auto_post_to_gmb
      ) {
        window.toast.fire({
          icon: "error",
          title: "Select at least one channel to post to.",
        });
        return;
      }

      if (
        settings.value.auto_post_to_gmb &&
        gmbConnected.value &&
        chosenLocations.length === 0
      ) {
        window.toast.fire({
          icon: "error",
          title: "Select at least one Google location.",
        });
        return;
      }

      await axios
        .post(
          axios.defaults.baseUrl + "updateDealerAutoPostingSettings",
          {
            auto_posting_enabled: settings.value.auto_posting_enabled ? 1 : 0,
            auto_post_to_facebook: settings.value.auto_post_to_facebook ? 1 : 0,
            auto_post_to_gmb: settings.value.auto_post_to_gmb ? 1 : 0,
            auto_post_gmb_locations: chosenLocations,
          },
          authHeaders()
        )
        .then((response) => {
          window.toast.fire({
            icon: response.data.status,
            title: response.data.message,
          });
        })
        .catch(() => {
          window.toast.fire({
            icon: "error",
            title: "Something went wrong, Please try again...",
          });
        });
    };

    onMounted(() => {
      loadSettings();
    });

    return {
      offered,
      facebookConnected,
      gmbConnected,
      facebookPageName,
      availableGmbLocations,
      selectedGmbLocationNames,
      settings,
      saveSettings,
    };
  },
};
</script>

<style scoped>
.badge-connected {
  color: #1f9d55;
  font-size: 12px;
  margin-left: 6px;
}
.badge-disconnected {
  color: #b91c1c;
  font-size: 12px;
  margin-left: 6px;
}
</style>
